import { useSession } from '@side/next-auth/react';
import { isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { normalizeAddressForMatch } from '@/common/utils/string';
import { type CoreRequest, useCore } from '$api-clients/hooks';

type Listing = CoreRequest<'/listings'>['Data']['results'][number];

const SEARCH_DEBOUNCE_MS = 300;

// Country terms Google appends to a prediction's secondary text. They are
// dropped before matching because a listing address never carries them.
const COUNTRY_TERMS = new Set(['usa', 'us', 'united states']);

// Non-locked listing statuses, as inclusion facets (listing facets can't
// exclude, so we list the statuses to keep).
export const STATUS_INCLUSION_FACET = [
  'active',
  'activeCont',
  'backUp',
  'comingSoon',
  'offMarket',
  'onMarket',
  'pending',
  'preListing',
]
  .map((status) => `status:${status}`)
  .join(',');

/**
 * A listing is eligible for offer matching when it is within its listing
 * window today. Agent membership, archived state and status are all filtered
 * server-side via the `GET /listings` facets.
 */
export const isListingEligibleForOfferMatch = (listing: Listing): boolean => {
  const { listingStartDate, listingEndDate } = listing;
  if (!listingStartDate || !listingEndDate) return false;

  const today = startOfDay(new Date());
  if (isBefore(today, startOfDay(parseISO(listingStartDate)))) return false;
  if (isAfter(today, startOfDay(parseISO(listingEndDate)))) return false;

  return true;
};

/**
 * Key used to compare a listing's address to a Google Places prediction.
 * Only street1 + city + state are compared: a prediction carries no ZIP, so
 * including it would prevent every match.
 */
export const getListingMatchKey = (
  property:
    | { street1?: string | null; city?: string | null; state?: string | null }
    | null
    | undefined,
): string => {
  if (!property) return '';
  return normalizeAddressForMatch(
    [property.street1, property.city, property.state].filter(Boolean).join(' '),
  );
};

export const getPredictionMatchKey = (
  prediction: google.maps.places.AutocompletePrediction,
): string => {
  const { main_text: mainText = '', secondary_text: secondaryText = '' } =
    prediction.structured_formatting ?? {};
  const secondaryParts = secondaryText
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && !COUNTRY_TERMS.has(part.toLowerCase()));
  return normalizeAddressForMatch([mainText, ...secondaryParts].join(' '));
};

/**
 * Fetches the current user's listings for the typed street address and returns
 * a map of normalized-address -> listingId for the ones eligible to link to a
 * new offer. Returns an empty map when disabled or when there is no query.
 */
export const useMatchingListings = (
  query: string | undefined,
  enabled: boolean,
): Map<string, string> => {
  const agentId = useSession()?.data?.user?.id;

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useDebounce(
    () => setDebouncedSearch(enabled ? (query ?? '').trim() : ''),
    SEARCH_DEBOUNCE_MS,
    [query, enabled],
  );

  const { data } = useCore(
    '/listings',
    enabled && debouncedSearch && agentId
      ? {
          params: {
            query: {
              search: debouncedSearch,
              facets: `agentId:${agentId},isArchived:false,${STATUS_INCLUSION_FACET}`,
              sort: 'createdAt:desc',
              limit: 20,
            },
          },
        }
      : null,
    { keepPreviousData: true },
  );

  return useMemo(() => {
    const matches = new Map<string, string>();
    if (!enabled) return matches;

    // Results arrive newest-first (createdAt:desc), so the first eligible match
    // for an address is the most recently created listing.
    for (const listing of data?.results ?? []) {
      if (isListingEligibleForOfferMatch(listing)) {
        const key = getListingMatchKey(listing.property);
        if (key && !matches.has(key)) {
          matches.set(key, listing.id);
        }
      }
    }
    return matches;
  }, [data, enabled]);
};
